import { Injectable } from '@angular/core';
import { ItemClassification } from '@boma/supplier-portal/shared/entities';
import { ItemClassificationsService } from '@boma/supplier-portal/shared/services';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { getSelectedSupplierId } from '../shared-state';

export interface ItemClassificationState {
  itemClassificationSearchResults: ItemClassification[];
  searchTerm: string | null;
  loading: boolean;
  error: string | null;
}

@Injectable()
export class ItemClassificationFacade {
  private readonly _entityId$: Observable<string>;
  private readonly _state$: BehaviorSubject<ItemClassificationState> =
    new BehaviorSubject<ItemClassificationState>({
      itemClassificationSearchResults: [],
      searchTerm: null,
      loading: false,
      error: null,
    });
  private readonly _searchTerms$ = new Subject<string>();

  loading$ = this._state$.pipe(map((state) => state.loading));
  error$ = this._state$.pipe(map((state) => state.error));
  itemClassificationSearchResults$ = this._state$.pipe(
    map((state) => state.itemClassificationSearchResults)
  );
  searchTerm$ = this._state$.pipe(map((state) => state.searchTerm));

  constructor(
    private store: Store,
    private itemClassificationsService: ItemClassificationsService
  ) {
    this._entityId$ = this.store
      .select(getSelectedSupplierId)
      .pipe(
        map((entityId) => entityId?.trim()),
        filter((entityId): entityId is string => !!entityId),
        distinctUntilChanged()
      );

    this._searchTerms$
      .pipe(
        map((searchTerm) => searchTerm?.trim() || ''),
        debounceTime(400),
        distinctUntilChanged(),
        tap((searchTerm) => {
          this._updateState({
            loading: searchTerm.length >= 2,
            error: null,
            searchTerm,
            itemClassificationSearchResults:
              searchTerm.length >= 2
                ? this._state$.value.itemClassificationSearchResults
                : [],
          });
        }),
        switchMap((searchTerm) => {
          if (searchTerm.length < 2) {
            return of([]);
          }

          return this._entityId$.pipe(
            take(1),
            switchMap((entityId) =>
              this.itemClassificationsService.searchItemClassificationCodes(
                entityId,
                searchTerm,
                0,
                20
              )
            ),
            catchError((error) => {
              this._updateState({ error: error?.message, loading: false });
              return of([]);
            })
          );
        })
      )
      .subscribe((results) => {
        this._updateState({
          itemClassificationSearchResults: results,
          loading: false,
        });
      });
  }

  searchItemClassifications(searchTerm: string): void {
    this._searchTerms$.next(searchTerm);
  }

  private _updateState(state: Partial<ItemClassificationState>) {
    this._state$.next({ ...this._state$.value, ...state });
  }
}
